import { useEffect, useState } from "react";
import { fetchPendingTimeTrackingRequestsCount } from "../api/TimeTracking/timeTrackingApi";
import { setPendingRequestsTabBadge } from "../utils/browserTabBadge";

const EVENT = "time-tracking-pending-count";
const EMPTY = { corrections: 0, timeOff: 0, total: 0 };

let lastCount = EMPTY;
let pollers = 0;
let intervalId = null;
let inFlight = false;

function readCached() {
  if (typeof window === "undefined") return EMPTY;
  return window.__ttPendingCount || lastCount;
}

function publish(next) {
  const normalized = {
    corrections: Number(next?.corrections) || 0,
    timeOff: Number(next?.timeOff) || 0,
    total: Number(next?.total) || 0,
  };
  lastCount = normalized;
  if (typeof window !== "undefined") {
    window.__ttPendingCount = normalized;
    window.dispatchEvent(new CustomEvent(EVENT, { detail: normalized }));
  }
  setPendingRequestsTabBadge(normalized.total);
}

async function refresh() {
  if (inFlight) return;
  inFlight = true;
  try {
    const next = await fetchPendingTimeTrackingRequestsCount();
    publish(next);
  } catch (err) {
    console.error("❌ Error cargando peticiones de control horario:", err);
  } finally {
    inFlight = false;
  }
}

function startPolling() {
  if (intervalId) return;
  refresh();
  intervalId = setInterval(refresh, 60000);
  if (typeof window !== "undefined") {
    window.addEventListener("time-tracking-pending-updated", refresh);
  }
}

function stopPolling() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (typeof window !== "undefined") {
    window.removeEventListener("time-tracking-pending-updated", refresh);
  }
  publish(EMPTY);
}

/**
 * Recuento de solicitudes pendientes de fichaje + vacaciones (RRHH).
 * Un único poller: el primer `poll: true` arranca; el resto solo se suscribe.
 */
export function usePendingTimeTrackingCount({ poll = false } = {}) {
  const [count, setCount] = useState(readCached);

  useEffect(() => {
    const handler = (event) => {
      setCount(event.detail || EMPTY);
    };
    window.addEventListener(EVENT, handler);
    setCount(readCached());
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  useEffect(() => {
    if (!poll) return undefined;
    pollers += 1;
    if (pollers === 1) startPolling();
    else refresh();
    return () => {
      pollers = Math.max(0, pollers - 1);
      if (pollers === 0) stopPolling();
    };
  }, [poll]);

  return count;
}
